import React from 'react';
import { SettingsLayoutHeader } from 'components/atoms/settingsLayoutHeader';
import { Box, Flex } from 'rebass/styled-components';

import { trackEvent } from 'helpers/analytics';
import { GoogleOAuthProvider } from '@react-oauth/google';
import usePageHook from '../../../hooks/usePageHook';
import { useIntegrationsPage } from './hooks/useIntegrationsPage';

import { LinkedinIntegrationCard } from './LinkedinIntegrationCard';
import { GoogleIntegrationCard } from './GoogleIntegrationCard';
import { GoogleIntegrationCardLegacy } from './GoogleIntegrationCardLegacy';
import { GoogleConnectModal } from './GoogleConnectModal';
import { PrivacyNote } from './PrivacyNote';
import { ShareContactsWarning } from './ShareContactsWarning';
import { SurveyCallout } from './SurveyCallout';

const IntegrationsContent = () => {
  usePageHook('Personal integrations');

  const {
    user,
    activeNetwork,
    googleSync,
    googleLogin,
    googleConnectModalOpen,
    initialConnectPreferences,
    openGoogleConnectModal,
    closeGoogleConnectModal,
    submitGooglePreferences,
    isCreatingGoogleAccount,
    isLaunchingGoogleOAuth,
    isUpdatingGoogleAccount,
    handleConfirmRemoveGoogle,
    handleConfirmRemoveLinkedin,
    hasGoogleSyncFeature,
    hasRelationshipStrength,
    isDisconnectingLinkedin,
    isDeletingGoogleAccount,
    isUpdatingLinkedinSync,
    linkedinSync,
    getLinkedinSyncLink,
    updateLinkedinSyncStatus,
    myNetworksLink,
    shouldShowShareContactsWarning,
  } = useIntegrationsPage();

  // `relationship_strength` gates the entire workspace surface. When the flag
  // is off, both the card UI/copy and the connect path stay on the legacy
  // contacts-only flow: legacy card markup + direct OAuth with the People-API
  // scope set the backend's `use_legacy_contacts_default?` branch expects.
  //
  // The card forwards the button's React SyntheticEvent into the click
  // handler, and `useGoogleLogin`'s return value accepts overrides options as
  // its first argument — wrap it in a zero-arg arrow so a stray event object
  // can never accidentally be parsed as overrides. Matches the pre-GET-268
  // call-site shape.
  const onGoogleConnect = hasRelationshipStrength ? openGoogleConnectModal : () => googleLogin();
  const GoogleCard = hasRelationshipStrength ? GoogleIntegrationCard : GoogleIntegrationCardLegacy;

  const description = hasGoogleSyncFeature
    ? "Sync your LinkedIn and Google data with Getro. Choose which networks to share your contacts with from the 'My networks' screen."
    : "Sync your LinkedIn data with Getro. Choose which networks to share your contacts with from the 'My networks' screen.";

  return (
    <>
      <Box maxWidth={['100%', '100%', '1200px']} width="100%" pr={['0px', '0px', '80px']}>
        <SettingsLayoutHeader title="Personal integrations" description={description} />
        <Flex sx={{ flexDirection: 'column', gap: '40px', pt: '24px', width: '100%' }}>
          <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
            {shouldShowShareContactsWarning && <ShareContactsWarning myNetworksLink={myNetworksLink} />}
            <LinkedinIntegrationCard
              linkedinSync={linkedinSync}
              getLinkedinSyncLink={getLinkedinSyncLink}
              onUpdateLinkedinSync={updateLinkedinSyncStatus}
              onRemoveConfirm={handleConfirmRemoveLinkedin}
              isRemoving={isDisconnectingLinkedin}
              isPausing={isUpdatingLinkedinSync}
            />
            {hasGoogleSyncFeature && (
              <GoogleCard
                googleSync={googleSync}
                onConnect={onGoogleConnect}
                onReconnect={onGoogleConnect}
                onRemoveConfirm={handleConfirmRemoveGoogle}
                isRemoving={isDeletingGoogleAccount}
              />
            )}
          </Flex>

          <PrivacyNote />
        </Flex>

        {hasRelationshipStrength && (
          <GoogleConnectModal
            isOpen={googleConnectModalOpen}
            isSubmitting={isCreatingGoogleAccount || isLaunchingGoogleOAuth || isUpdatingGoogleAccount}
            initialPreferences={initialConnectPreferences}
            onCancel={closeGoogleConnectModal}
            onSubmit={submitGooglePreferences}
          />
        )}

        <SurveyCallout
          user={user}
          activeNetwork={activeNetwork}
          onTrack={() => trackEvent('user_account:more_integrations_feedback')}
        />
      </Box>
    </>
  );
};

export const Integrations = () => (
  <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
    <IntegrationsContent />
  </GoogleOAuthProvider>
);
